import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import type { AssetAlternativeId } from "@shared/types/content-api";
import {
  epgMappingOf,
  epgMappingStatus,
  readEpgChannelId,
  type EpgMappingIssue,
} from "@shared/epg-mapping";
import { foyerApi } from "../../http/foyer";
import { describeError, useToast } from "../toast";
import { Trans, useTranslation } from "react-i18next";
import { translateKey, type Translate } from "../../i18n/translate";
import { tokens } from "../../theme";
import { SectionHead } from "./editorBits";

/**
 * The channel's EPG source id — the field that decides whether it has a
 * programme guide at all (`BE-22`).
 *
 * ## Why this is a confirm dialog and not an input with a Save button
 *
 * Because it cannot be undone. `nenda-content-api` upserts `alternativeIds` and
 * never deletes them, there is no removal route, and `(source, alternativeId)`
 * is globally unique — so one mistyped digit both sticks to this channel for
 * ever AND permanently blocks the channel the id really belongs to from ever
 * claiming it. That service is frozen and read-only to Foyer, so the trap
 * cannot be removed; the only honest response is to make the write deliberate
 * and to say plainly what it costs.
 *
 * Once set, the field is READ-ONLY. Rendering an editable box over a value the
 * backend will refuse to change is worse than rendering none: it invites the
 * exact action that produces a support case nobody can fix.
 *
 * ## Why an unmapped channel is a warning and not a validation error
 *
 * A channel with genuinely no guide is a legitimate state — an in-house
 * information loop has no schedule and never will. What is NOT legitimate is
 * not knowing: today an unmapped channel is indistinguishable from a mapped one
 * whose feed was empty, and the only trace of the difference is a log line in a
 * service with its structured reports disabled.
 */

function issueText(t: Translate, issue: EpgMappingIssue): string {
  switch (issue.code) {
    case "blank":
    case "has_whitespace":
    case "control_characters":
    case "too_long":
      return translateKey(t, `channels:channelEpgMapping.issue.${issue.code}`);
    default:
      return issue.code;
  }
}

export default function ChannelEpgMapping({
  assetId,
  alternativeIds,
  onMapped,
}: {
  assetId: string;
  /** From the channel row. `undefined` means "not loaded", not "none". */
  alternativeIds: AssetAlternativeId[] | undefined;
  onMapped?: (mapping: string) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["channels", "common"]);
  const [saved, setSaved] = useState<string | null>(null);
  const [value, setValue] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const status = epgMappingStatus({ alternativeIds });
  const current = saved ?? epgMappingOf(alternativeIds);
  const read = readEpgChannelId(value);
  const issue =
    value.trim() === "" ? null : "issue" in read ? read.issue : null;

  async function commit() {
    setBusy(true);
    setError(null);
    try {
      const answer = await foyerApi.setChannelEpgMapping(
        assetId,
        "issue" in read ? value : read.value
      );
      setSaved(answer.mapping);
      setConfirming(false);
      setValue("");
      toast.success(t("channelEpgMapping.mapped", { mapping: answer.mapping }));
      onMapped?.(answer.mapping);
    } catch (e) {
      const err = e as {
        code?: string;
        message: string;
        holder?: { assetId: string; originalTitle: string };
        current?: string;
      };
      setError(
        err.code === "mapping_taken"
          ? t("channelEpgMapping.errors.mappingTaken", {
              holder:
                err.holder?.originalTitle ??
                t("channelEpgMapping.errors.anotherAsset"),
              assetId: err.holder?.assetId,
            })
          : err.code === "mapping_is_permanent"
            ? t("channelEpgMapping.errors.mappingPermanent", {
                current: err.current,
              })
            : err.code === "invalid_epg_channel_id"
              ? t("channelEpgMapping.errors.invalidId")
              : err.message
      );
      setConfirming(false);
      toast.error(describeError(t, "mapChannelToEpg", e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box data-testid="channel-epg-mapping">
      <SectionHead
        title={t("channelEpgMapping.sectionTitle")}
        subtitle={t("channelEpgMapping.sectionSubtitle")}
        aside={
          current ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={t("channelEpgMapping.chipMapped")}
              data-testid="epg-mapping-set"
            />
          ) : status === "unmapped" ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t("channelEpgMapping.chipNoGuide")}
              data-testid="epg-mapping-missing"
            />
          ) : null
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, typography: "meta" }}>
          {error}
        </Alert>
      )}

      {current ? (
        <Box>
          <Typography
            variant="bodySm"
            sx={{ fontFamily: "fontFamilyMono", color: tokens.ink }}
            data-testid="epg-mapping-value"
          >
            {current}
          </Typography>
          {/* No edit affordance, and the sentence says why rather than
              leaving somebody hunting for the button. */}
          <Typography variant="micro" sx={{ color: tokens.inkMuted, mt: 0.5 }}>
            {t("channelEpgMapping.permanentNote")}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {status === "unmapped" && (
            <Alert
              severity="warning"
              sx={{ typography: "meta" }}
              data-testid="epg-mapping-warning"
            >
              {t("channelEpgMapping.unmappedWarning")}
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              fullWidth
              label={t("channelEpgMapping.fieldLabel")}
              placeholder={t("channelEpgMapping.fieldPlaceholder")}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              error={Boolean(issue)}
              helperText={
                issue ? issueText(t, issue) : t("channelEpgMapping.fieldHint")
              }
              inputProps={{ "data-testid": "epg-mapping-input" }}
            />
            <Button
              variant="contained"
              size="small"
              sx={{ mt: 0.5 }}
              disabled={busy || value.trim() === "" || Boolean(issue)}
              onClick={() => setConfirming(true)}
              data-testid="epg-mapping-save"
            >
              {t("channelEpgMapping.set")}
            </Button>
          </Stack>
        </Stack>
      )}

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>{t("channelEpgMapping.confirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ typography: "bodySm" }}>
            <Trans
              t={t}
              i18nKey="channelEpgMapping.confirmMapping"
              values={{ value: "issue" in read ? value.trim() : read.value }}
              components={{
                mono: (
                  <Box component="span" sx={{ fontFamily: "fontFamilyMono" }} />
                ),
              }}
            />
          </DialogContentText>
          <DialogContentText sx={{ typography: "bodySm", mt: 1.5 }}>
            <Trans
              t={t}
              i18nKey="channelEpgMapping.confirmWarning"
              components={{ b: <b /> }}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busy}
            onClick={() => void commit()}
            data-testid="epg-mapping-confirm"
          >
            {t("channelEpgMapping.setPermanently")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
